/**
 * Fixture time for the product vignettes.
 *
 * A vignette shows the real app components, and they print time relative to
 * now: "Updated just now", "12m ago", "Yesterday", "due in 5 days". A fixture
 * with fixed dates would drift out of that story a day after it was written, so
 * every fixture time is an offset from the moment the page renders, in both of
 * the shapes the app reads:
 *
 * - `naive`: a 'Y-m-d H:i:s' wall-clock string in the app's timezone, the way
 *   the passport store writes timestamps (read by lib/serverTime.js:
 *   relativeTime, shortDate, dayLabel, clockTime).
 * - `iso`: an absolute ISO-8601 instant (read with date-fns / Date by the data
 *   request, import and events screens).
 *
 * `day` is the naive date alone, for `date` property values.
 */

/** config/app.php `timezone`, what HandleInertiaRequests shares as `appTimezone`. */
export const DEFAULT_TIMEZONE = 'Europe/Madrid';

const UNIT_MS = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
};

const pad = (value) => String(value).padStart(2, '0');

/**
 * The wall-clock parts of an instant in a zone. An unknown zone falls back to
 * the viewer's own clock, as lib/serverTime.js does, so the two stay agreed.
 */
function partsIn(date, timeZone) {
    try {
        const parts = Object.fromEntries(
            new Intl.DateTimeFormat('en-US', {
                timeZone,
                hourCycle: 'h23',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
            }).formatToParts(date).map(({ type, value }) => [type, value]),
        );

        return { y: +parts.year, M: +parts.month, d: +parts.day, h: +parts.hour, m: +parts.minute, s: +parts.second };
    } catch {
        return {
            y: date.getFullYear(), M: date.getMonth() + 1, d: date.getDate(),
            h: date.getHours(), m: date.getMinutes(), s: date.getSeconds(),
        };
    }
}

/** An instant as the store's naive 'Y-m-d H:i:s' string in `timeZone`. */
export function wallClock(date, timeZone = DEFAULT_TIMEZONE) {
    const { y, M, d, h, m, s } = partsIn(date, timeZone);

    return `${y}-${pad(M)}-${pad(d)} ${pad(h)}:${pad(m)}:${pad(s)}`;
}

function offsetMs(offset) {
    return Object.entries(offset ?? {}).reduce((total, [unit, amount]) => {
        if (!(unit in UNIT_MS)) {
            throw new Error(`Unknown time unit "${unit}"; use ${Object.keys(UNIT_MS).join(', ')}`);
        }

        return total + Number(amount) * UNIT_MS[unit];
    }, 0);
}

function moment(date, timeZone) {
    const naive = wallClock(date, timeZone);

    return { date, iso: date.toISOString(), naive, day: naive.slice(0, 10) };
}

/**
 * A moment `offset` before `now`, e.g. `ago({ minutes: 12 })` or
 * `ago({ days: 2, hours: 3 })`. Returns `{ date, iso, naive, day }`.
 */
export function ago(offset, { timeZone = DEFAULT_TIMEZONE, now = new Date() } = {}) {
    return moment(new Date(now.getTime() - offsetMs(offset)), timeZone);
}

/** A moment `offset` after `now` (a due date, an expiry). Same shape as ago(). */
export function ahead(offset, { timeZone = DEFAULT_TIMEZONE, now = new Date() } = {}) {
    return moment(new Date(now.getTime() + offsetMs(offset)), timeZone);
}

/** A fixed instant (an ISO-8601 string) in ago()'s shape: a time that does not move with the day it is read. */
export function at(iso, { timeZone = DEFAULT_TIMEZONE } = {}) {
    return moment(new Date(iso), timeZone);
}

/**
 * ago() and ahead() pinned to one zone and one `now`, so every time in a
 * dataset is measured from the same instant and two fixtures built from one
 * clock never disagree about what "just now" was; at() in the same zone, for
 * the times that are fixed instead.
 */
export function createClock({ timeZone = DEFAULT_TIMEZONE, now = new Date() } = {}) {
    return {
        timeZone,
        now,
        ago: (offset) => ago(offset, { timeZone, now }),
        ahead: (offset) => ahead(offset, { timeZone, now }),
        at: (iso) => at(iso, { timeZone }),
    };
}

/** config/id.php `epoch_ms` (2025-01-01T00:00:00Z), as lib/snowflake.js reads it. */
const EPOCH_MS = 1735689600000n;

/**
 * A snowflake id minted at a fixed instant: `{prefix}_{16 hex}` laid out as
 * [41 bits ms since the epoch][10 bits worker][12 bits sequence], the layout
 * lib/snowflake.js decodes. Fixture ids are fixed (a test can name them), so
 * they take an absolute time rather than a clock.
 */
export function snowflakeAt(prefix, when, sequence = 0, worker = 3) {
    const ms = BigInt(new Date(when).getTime()) - EPOCH_MS;
    const value = (ms << 22n) | (BigInt(worker) << 12n) | BigInt(sequence);

    return `${prefix}_${value.toString(16).padStart(16, '0')}`;
}
